import { useGetPreSignedUploadUrlMutation } from '@/redux/attachment/attachment.api.slice';
import {
  AttachmentType,
  IAttachmentCategory,
  AttachmentParentType,
} from '@/redux/attachment/attachment.d';
import hashFile from '@/lib/xxhash';
import { uploadFileToPresignedUrl } from '@/pages/UploadV2/utils/postSync';
import {
  useCreateProjectGroupMutation,
  useUpdateProjectGroupMutation,
} from './projectGroup.api.slice';

interface CreateGroupInput {
  name: string;
  description?: string;
  category: string;
  difficulty?: string;
  tags?: string[];
  projects: string[];
  thumbnail?: File | null;
}

/** Create a multi workpiece: the group, then its thumbnail, then the group again
 * to point at the uploaded attachment. Shared by the standalone create page and
 * the V2 wizard's multi-piece screen, so the two can't drift. */
export default function useCreateProjectGroup() {
  const [createProjectGroup] = useCreateProjectGroupMutation();
  const [updateProjectGroup] = useUpdateProjectGroupMutation();
  const [getPreSignedUploadUrl] = useGetPreSignedUploadUrlMutation();

  // fallow-ignore-next-line complexity
  return async function createGroup(input: CreateGroupInput): Promise<string> {
    const created = await createProjectGroup({
      name: input.name.trim(),
      description: input.description?.trim() || undefined,
      tags: input.tags?.length ? input.tags : undefined,
      category: input.category,
      difficulty: input.difficulty,
      projects: input.projects,
    }).unwrap();

    const groupId = created?.group?._id;
    if (!groupId) throw new Error('Failed to retrieve group ID');

    if (!input.thumbnail) return groupId;

    const fileHash = await hashFile(input.thumbnail);
    const [signed] = await getPreSignedUploadUrl({
      attachments: [
        {
          parentId: groupId,
          parentType: AttachmentParentType.PROJECT_GROUP,
          category: IAttachmentCategory.projectGroupThumbnail,
          fileName: input.thumbnail.name,
          fileHash: fileHash.toString(),
          fileSize: input.thumbnail.size,
          resourceType: AttachmentType.image,
        },
      ],
    }).unwrap();

    // No signed URL means the hash already exists server-side; the attachment id
    // still comes back and still has to be attached.
    if (signed?.signedUrl) {
      await uploadFileToPresignedUrl(input.thumbnail, signed.signedUrl);
    }
    if (signed?.attachmentId) {
      await updateProjectGroup({
        id: groupId,
        data: { thumbnail: signed.attachmentId.toString() },
      }).unwrap();
    }

    return groupId;
  };
}
